/**
 * Verify-email screen — 6-digit OTP entry, POST /auth/email/verify.
 *
 * CONTRACT (unchanged — VerifyEmailScreen.test.tsx covers it):
 *   - Reads `user_id` (+ optional `email`) from the query, written by
 *     SignupScreen on success. Missing `user_id` renders the deep-link
 *     guidance instead of a form.
 *   - Six-cell OTP with auto-advance and paste; a complete code auto-submits.
 *   - On 200: store the access_token and go to the safe `return_to`, else /chat.
 *   - On 422: surface the localized error and clear the cells (focus returns
 *     to cell 0 via OtpInput's value-reset effect).
 *   - "Resend code" calls /auth/email/resend and starts a 30s countdown, with
 *     the same neutral notice /forgot-password uses (no account enumeration).
 *
 * ── The counter rebuild (2026-08) ────────────────────────────────────────────
 *
 * A one-time code IS data, so this is the one auth screen where the instrument
 * face earns its place: the cells are `.g-data` (IBM Plex Mono, tabular, dotted
 * zero) inside a recessed well, reading as the stub of a slip rather
 * than as six more text boxes. The decorative mail icon disc is gone.
 *
 * The submit button is no longer disabled until six digits are present. A
 * disabled button with no explanation is a dead end — the commonest way a form
 * silently strands someone. Pressing it with an incomplete code now says what
 * is wrong ("enter 6 digits") and puts focus back in the first cell.
 *
 * The countdown renders in `.g-data` too: it is a measured quantity, and the
 * tabular figures stop the label jittering as it ticks 30 → 9.
 */
import { Info, Loader2, MailCheck } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Stagger, StaggerItem } from "@/components/motion";
import { AuthCounter } from "@/features/auth/components/AuthCounter";
import {
  AUTH_LINK_CLASS,
  AuthLink,
  AuthSubmit,
} from "@/features/auth/components/controls";
import {
  OtpInput,
  type OtpInputHandle,
} from "@/features/auth/components/OtpInput";
import { Slip, SlipNotice } from "@/features/auth/components/slip";
import { safeReturnToOrNull, withReturnTo } from "@/features/auth/destination";
import { OTP_LENGTH } from "@/features/auth/schemas";
import { ApiError, NetworkError, apiClient, AUTH_PATHS } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import { ICON, ICON_STROKE, iconProps } from "@/lib/icons";

const RESEND_COUNTDOWN_SECONDS = 30;

interface EmailVerifyResponse {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
  redirect: "/chat";
}

interface EmailResendResponse {
  user_id: string;
  email_pending_verification: boolean;
}

function mapVerifyError(err: unknown): string {
  if (err instanceof NetworkError) return "errors.network";
  if (!(err instanceof ApiError)) return "errors.generic";
  switch (err.code) {
    case "auth.otp.expired":
      return "errors.auth.otp.expired";
    case "auth.otp.max_attempts":
      return "errors.auth.otp.max_attempts";
    case "auth.otp.invalid":
      return "errors.auth.otp.invalid";
    case "auth.otp.not_found":
      return "errors.auth.otp.not_found";
    case "auth.signup.invalid_input":
      return "errors.auth.signup.invalid_input";
    default:
      return "errors.generic";
  }
}

export function VerifyEmailScreen(): ReactElement {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const [searchParams] = useSearchParams();

  const userId = searchParams.get("user_id");
  const email = searchParams.get("email");
  // NON-PROD: SignupScreen forwards the echoed OTP here so a QA tester can see
  // the code on-screen (no real email delivery). Always absent in production.
  const devOtp = searchParams.get("dev_otp");
  const rawReturnTo = searchParams.get("return_to");

  const [otp, setOtp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(0);
  const otpRef = useRef<OtpInputHandle | null>(null);

  // 30s resend countdown — counts down once per second after a successful
  // resend. The button is disabled during the countdown and the remaining
  // seconds are visible.
  useEffect(() => {
    if (countdown <= 0) return;
    const handle = window.setTimeout(() => setCountdown(countdown - 1), 1000);
    return (): void => window.clearTimeout(handle);
  }, [countdown]);

  const submitOtp = useCallback(
    async (full: string): Promise<void> => {
      if (userId === null) return;
      setSubmitting(true);
      setErrorKey(null);
      try {
        const data = await apiClient.post<
          EmailVerifyResponse,
          { user_id: string; otp: string }
        >(
          AUTH_PATHS.emailVerify,
          { user_id: userId, otp: full },
          { skipRefreshOn401: true },
        );
        setAccessToken(data.access_token);
        // W0.1: back to where sign-in started (a guest's `/chat?t=<thread>`),
        // the assistant otherwise.
        navigate(safeReturnToOrNull(rawReturnTo) ?? "/chat", { replace: true });
      } catch (err) {
        setErrorKey(mapVerifyError(err));
        // Clear the OTP + return focus to cell 0.
        setOtp("");
      } finally {
        setSubmitting(false);
      }
    },
    [userId, setAccessToken, navigate, rawReturnTo],
  );

  const onResend = useCallback(async (): Promise<void> => {
    if (userId === null) return;
    if (countdown > 0) return;
    setResendBusy(true);
    setResendNotice(null);
    try {
      await apiClient.post<EmailResendResponse, { user_id: string }>(
        AUTH_PATHS.emailResend,
        { user_id: userId },
        { skipRefreshOn401: true },
      );
      setCountdown(RESEND_COUNTDOWN_SECONDS);
      setResendNotice("forgotPassword.neutralMessage");
    } catch {
      // Don't surface specific resend errors — the neutral notice
      // matches the no-enumeration pattern from /forgot-password.
      setResendNotice("forgotPassword.neutralMessage");
    } finally {
      setResendBusy(false);
    }
  }, [userId, countdown]);

  const onManualSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (submitting) return;
    if (otp.length !== OTP_LENGTH) {
      // Never strand the user behind a disabled button: name the problem and
      // put the caret where they can fix it.
      setErrorKey("common.otp.format");
      otpRef.current?.focusFirst();
      return;
    }
    void submitOtp(otp);
  };

  if (userId === null) {
    return (
      <AuthCounter
        door="consumer"
        doorName={t("verifyEmail.doorName")}
        title={t("verifyEmail.title")}
        testId="verify-email-missing"
      >
        <div className="gav2-stack-md flex flex-col">
          <SlipNotice tone="attention" icon={<Info {...iconProps("sm")} />}>
            {t("verifyEmail.missingUserId")}
          </SlipNotice>
          <AuthLink to={withReturnTo("/signup", rawReturnTo)}>{t("signup.title")}</AuthLink>
        </div>
      </AuthCounter>
    );
  }

  return (
    <AuthCounter
      door="consumer"
      doorName={t("verifyEmail.doorName")}
      title={t("verifyEmail.title")}
      testId="verify-email-screen"
      lede={
        email !== null && email !== "" ? (
          <>
            {t("verifyEmail.lede")}{" "}
            <strong className="font-semibold text-[var(--g-ink)]">{email}</strong>
          </>
        ) : (
          t("verifyEmail.ledeUnknownEmail")
        )
      }
    >
      <Stagger className="gav2-stack-lg flex flex-col">
        {devOtp !== null && devOtp !== "" ? (
          <StaggerItem>
            <SlipNotice
              tone="neutral"
              role="status"
              testId="verify-email-dev-hint"
              icon={<MailCheck {...iconProps("sm")} />}
            >
              <span>{t("verifyEmail.devHint")} </span>
              <b className="g-data text-base tracking-[0.2em] text-[var(--g-figure)]">
                {devOtp}
              </b>
            </SlipNotice>
          </StaggerItem>
        ) : null}

        <StaggerItem>
          <form
            className="gav2-stack-md flex flex-col"
            onSubmit={onManualSubmit}
            noValidate
            aria-describedby={errorKey !== null ? "verify-email-error" : undefined}
          >
            <OtpInput
              ref={otpRef}
              value={otp}
              onChange={(next) => {
                setOtp(next);
                // Don't clear the error while typing — the user can see the
                // previous attempt's message while correcting.
              }}
              onComplete={(full) => {
                if (!submitting) {
                  void submitOtp(full);
                }
              }}
              label={t("verifyEmail.otpLabel")}
              disabled={submitting}
              invalid={errorKey !== null}
              errorId={errorKey !== null ? "verify-email-error" : undefined}
              testId="verify-otp-cell"
            />

            {errorKey !== null ? (
              <p
                id="verify-email-error"
                role="alert"
                className="text-[0.8125rem] font-medium text-[var(--g-error)]"
                data-testid="verify-email-error"
              >
                {t(errorKey)}
              </p>
            ) : null}

            <AuthSubmit
              pending={submitting}
              pendingLabel={t("verifyEmail.submitting")}
              testId="verify-email-submit"
            >
              {t("verifyEmail.submit")}
            </AuthSubmit>
          </form>
        </StaggerItem>

        <StaggerItem>
          <Slip>
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
              <span className="text-[0.6875rem] font-bold uppercase tracking-[0.09em] text-[var(--g-ink-2)]">
                {t("verifyEmail.resendLabel")}
              </span>
              <button
                type="button"
                onClick={onResend}
                disabled={resendBusy || countdown > 0 || submitting}
                className={
                  "inline-flex min-h-[2.75rem] items-center text-[0.8125rem] disabled:opacity-60 " +
                  AUTH_LINK_CLASS
                }
                data-testid="verify-email-resend"
              >
                {countdown > 0 ? (
                  // One interpolated string, never a prefix + figure + unit
                  // sandwich: the number sits at the START of the Kyrgyz
                  // sentence and at the END of the Russian one, so splitting
                  // the key would hard-code Russian word order.
                  <span className="text-[var(--g-ink-2)]">
                    {t("verifyEmail.resendCountdown", { seconds: countdown })}
                  </span>
                ) : resendBusy ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2
                      size={ICON.sm}
                      strokeWidth={ICON_STROKE}
                      className="g-spin"
                      aria-hidden="true"
                    />
                    {t("verifyEmail.resending")}
                  </span>
                ) : (
                  t("verifyEmail.resend")
                )}
              </button>
            </div>
            {resendNotice !== null ? (
              <p
                className="px-4 py-2.5 text-[0.75rem] leading-snug text-[var(--g-ink-2)]"
                data-testid="verify-email-resend-notice"
              >
                {t(resendNotice)}
              </p>
            ) : null}
          </Slip>
        </StaggerItem>
      </Stagger>
    </AuthCounter>
  );
}

export default VerifyEmailScreen;
